import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Login from "./Login";
import { useAppSelector, useIsMobile } from "../app/hooks";
import { selectUserRole } from "../slices/user";
import DesktopLayout from "../components/DesktopLayout";
import AccessKeys from "./AccessKeys";
import Users from "./Users";
import { selectToken } from "../slices/token";
import Logbook from "./Logbook";
import AccessPoints from "./AccessPoints";
import Units from "./Units";
import HostUsers from "./HostUsers";
import SecurityGuards from "./SecurityGuards";
import NetworkAdmins from "./NetworkAdmins";
import Properties from "./Properties";
import AccessGroups from "./AccessGroups";
import Error from "../components/Error";
import SessionExpiredModal from "../components/SessionExpiredModal";
import AccessKey from "./AccessKey";
import Logs from "./Logs";
import MobileLayout from "../components/MobileLayout";
import MobAccessKeys from "./MobAccessKeys";
import MobLogbook from "./MobLogbook";
import MobUsers from "./MobUsers";
import MobUnits from "./MobUnits";
import SetPassword from "./SetPassword";
import ResetPassword from "./ResetPassword";
import MobLogs from "./MobLogs";
import MobileAccessKeyDetails from "./MobileAccessKeyDetails";
import MobileNewAccessKey from "./MobileNewAccessKey";
import MobileRegisterDetails from "./MobileRegisterDetails";
import MobileUnitDetails from "./MobileUnitDetails";
import MobileNewUnit from "./MobileNewUnit";
import MobileEditUnit from "./MobileEditUnit";
import MobileNewUser from "./MobileNewUser";
import MobileEditUser from "./MobileEditUser";
import MobileUserDetails from "./MobileUserDetails";
import PassAuthorization from "./PassAuthorization";

const renderPublicRoutes = (isMobile: boolean) => (
  <>
    <Route path="/login" element={<Login isMobile={isMobile} />} />
    <Route path="/my-access-key/:uuid" element={<AccessKey />} />
    <Route path="/authorization_pass" element={<PassAuthorization />} />
    <Route path="/password" element={<SetPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
  </>
);

const AppRouter = () => {
  const token = useAppSelector(selectToken);
  const isMobile = useIsMobile();
  const userRole = useAppSelector(selectUserRole);
  const isSecondaryHostRestricted =
    userRole === "secondary_host";

  if (isSecondaryHostRestricted) {
    return (
      <BrowserRouter>
        <Routes>
          {renderPublicRoutes(isMobile)}
          {token ? (
            <Route element={isMobile ? <MobileLayout /> : <DesktopLayout />}>
              <Route path="/access-keys" element={isMobile ? <MobAccessKeys /> : <AccessKeys />} />
              <Route path="/logbook" element={isMobile ? <MobLogbook /> : <Logbook />} />
              {isMobile && <>
                <Route path="/access-key-details/:id" element={<MobileAccessKeyDetails />} />
                <Route path="/new-access-key" element={<MobileNewAccessKey />} />
                <Route path="/access-details/:id" element={<MobileRegisterDetails />} />
              </>}
              <Route path="*" element={<Navigate to="/access-keys" replace />} />
            </Route>
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
        <Error />
        <SessionExpiredModal />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {renderPublicRoutes(isMobile)}
        {token ? (
          <>
            {!isMobile ? (
              <Route element={<DesktopLayout />}>
                <Route path="/access-keys" element={<AccessKeys />} />
                <Route path="/logbook" element={<Logbook />} />
                <Route path="/users" element={<Users />} />
                <Route path="/access-points" element={<AccessPoints />} />
                <Route path="/units" element={<Units />} />
                <Route path="/host-users" element={<HostUsers />} />
                <Route path="/security-guards" element={<SecurityGuards />} />
                <Route path="/network-admins" element={<NetworkAdmins />} />
                <Route path="/properties" element={<Properties />} />
                <Route path="/access-groups" element={<AccessGroups />} />
                <Route path="/logs" element={<Logs />} />
                <Route
                  path="*"
                  element={<Navigate to="/access-keys" replace />}
                />
              </Route>
            ) : (
              <Route element={<MobileLayout />}>
                <Route path="/access-keys" element={<MobAccessKeys />} />
                <Route
                  path="/access-key-details/:id"
                  element={<MobileAccessKeyDetails />}
                />
                <Route
                  path="/new-access-key"
                  element={<MobileNewAccessKey />}
                />
                <Route path="/logbook" element={<MobLogbook />} />
                <Route
                  path="/access-details/:id"
                  element={<MobileRegisterDetails />}
                />
                <Route path="/users" element={<MobUsers />} />
                <Route path="/new-user" element={<MobileNewUser />} />
                <Route
                  path="/user-details/:id"
                  element={<MobileUserDetails />}
                />
                <Route
                  path="/edit-user/:id/:user_role"
                  element={<MobileEditUser />}
                />
                <Route path="/units" element={<MobUnits />} />
                <Route
                  path="/unit-details/:id/:prop_id"
                  element={<MobileUnitDetails />}
                />
                <Route path="/new-unit" element={<MobileNewUnit />} />
                <Route path="/edit-unit/:id" element={<MobileEditUnit />} />
                <Route path="/logs" element={<MobLogs />} />
                <Route
                  path="*"
                  element={<Navigate to="/access-keys" replace />}
                />
              </Route>
            )}
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
      <Error />
      <SessionExpiredModal />
    </BrowserRouter>
  );
};

export default AppRouter;
